import {
  IconButton,
  IconButtonBold,
  IconBuzzer,
  IconBuzzerBold,
  IconClose,
  IconController,
  IconControllerBold,
  IconLED,
  IconLEDBold,
  IconNFCReader,
  IconNFCReaderBold,
  IconScreen,
  IconScreenBold,
} from '@/icons';
import { Button } from '@/ui/Button/Button';
import { T } from '@/ui/Text/Text';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useClickAway } from 'react-use';
import { useHardwareSensorManager } from '../customHooks/useHardwareSensorManager';
import { GeneralSettingsType } from '../SettingsSidebar/types';
import ButtonHardwareSettings from './HardwareSettingsContent/ButtonHardwareSettings';
import BuzzerHardwareSettings from './HardwareSettingsContent/BuzzerHardwareSettings';
import ControllerHardwareSettings from './HardwareSettingsContent/ControllerHardwareSettings';
import LEDHardwareSettings from './HardwareSettingsContent/LEDHardwareSettings';
import NFCReaderHardwareSettings from './HardwareSettingsContent/NFCReaderHardwareSettings';
import ScreenHardwareSettings from './HardwareSettingsContent/ScreenHardwareSettings';
import {
  StyledBackdrop,
  StyledContent,
  StyledContentContainer,
  StyledHardwareNav,
  StyledHardwareNavItem,
  StyledModalContainer,
  StyledTitle,
} from './styles';

interface HardwareSettingsModalProps {
  open: boolean;
  setIsHardwareModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  settings: GeneralSettingsType;
  setSettings: React.Dispatch<React.SetStateAction<GeneralSettingsType>>;
}

function HardwareSettingsModal({
  open,
  setIsHardwareModalOpen,
  settings,
  setSettings,
}: HardwareSettingsModalProps) {
  // Destructure values from the custom hook `useHardwareSensorManager`
  // to determine if more sensors of specific types can be added.
  const { moreNexmoSensorsAllowed, moreTHSensorsAllowed, moreLEDsAllowed } =
    useHardwareSensorManager(settings);

  // --- Hardware Components ---
  // for mapping hardware components to their respective settings
  // and icons, allowing for dynamic rendering in the modal.
  const hardwareComponents = {
    Controllers: {
      icon: <IconController />,
      iconBold: <IconControllerBold />,
      component: (
        <ControllerHardwareSettings
          controllers={settings.controllers}
          setControllers={(newControllers) =>
            setSettings((prev) => ({
              ...prev,
              controllers: newControllers,
            }))
          }
        />
      ),
    },
    'NFC Readers': {
      icon: <IconNFCReader />,
      iconBold: <IconNFCReaderBold />,
      component: (
        <NFCReaderHardwareSettings
          nfcReaders={settings.nfcReaders}
          setNfcReaders={(newNfcReaders) =>
            setSettings((prev) => ({
              ...prev,
              nfcReaders: newNfcReaders,
            }))
          }
          moreNexmoNFCAllowed={moreNexmoSensorsAllowed}
          moreTHNFCAllowed={moreTHSensorsAllowed}
        />
      ),
    },
    Buttons: {
      icon: <IconButton />,
      iconBold: <IconButtonBold />,
      component: (
        <ButtonHardwareSettings
          buttons={settings.buttons}
          setButtons={(newButtons) =>
            setSettings((prev) => ({
              ...prev,
              buttons: newButtons,
            }))
          }
          moreButtonsAllowed={moreNexmoSensorsAllowed}
        />
      ),
    },
    Buzzers: {
      icon: <IconBuzzer />,
      iconBold: <IconBuzzerBold />,
      component: (
        <BuzzerHardwareSettings
          buzzers={settings.buzzers}
          setBuzzers={(newBuzzers) =>
            setSettings((prev) => ({
              ...prev,
              buzzers: newBuzzers,
            }))
          }
          moreBuzzersAllowed={moreNexmoSensorsAllowed}
        />
      ),
    },
    LEDs: {
      icon: <IconLED />,
      iconBold: <IconLEDBold />,
      component: (
        <LEDHardwareSettings
          leds={settings.leds}
          setLeds={(newLeds) =>
            setSettings((prev) => ({
              ...prev,
              leds: newLeds,
            }))
          }
          moreLEDsAllowed={moreLEDsAllowed}
        />
      ),
    },
    Screens: {
      icon: <IconScreen />,
      iconBold: <IconScreenBold />,
      component: (
        <ScreenHardwareSettings
          screens={settings.screens}
          setScreens={(newScreens) =>
            setSettings((prev) => ({
              ...prev,
              screens: newScreens,
            }))
          }
        />
      ),
    },
  } as const;

  // --- Type for Tab Keys ---
  type TabKey = keyof typeof hardwareComponents;

  // --- State Management ---
  const modalRef = useRef<HTMLDivElement>(null);
  const tabs = useMemo(
    () => Object.keys(hardwareComponents) as TabKey[],
    [hardwareComponents],
  );

  const [activeTab, setActiveTab] = useState<TabKey>('Controllers');
  const intl = useIntl();

  const ActiveComponent = hardwareComponents[activeTab].component;

  // Effect to close the modal when clicking outside of it
  useClickAway(modalRef, () => {
    setIsHardwareModalOpen(false);
  });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsHardwareModalOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [setIsHardwareModalOpen]);

  if (!open) return null;

  // --- JSX ---
  return (
    <StyledBackdrop>
      <StyledModalContainer
        ref={modalRef}
        data-testid="hardware-settings-modal"
      >
        <StyledTitle>
          <T h3>
            {intl.formatMessage({
              defaultMessage: 'Hardware Settings',
              id: '2gqLNJ',
            })}
          </T>
          <Button
            mode="transparent"
            size="minimum"
            onClick={() => setIsHardwareModalOpen(false)}
          >
            <IconClose />
          </Button>
        </StyledTitle>
        <StyledContentContainer>
          <StyledHardwareNav>
            {tabs.map((key) => (
              <StyledHardwareNavItem
                key={key}
                onClick={() => setActiveTab(key)}
                active={activeTab === key}
              >
                {activeTab === key
                  ? hardwareComponents[key].iconBold
                  : hardwareComponents[key].icon}
                <T p {...(activeTab === key ? { bold: true } : {})}>
                  {key}
                </T>
              </StyledHardwareNavItem>
            ))}
          </StyledHardwareNav>
          <StyledContent>{ActiveComponent}</StyledContent>
        </StyledContentContainer>
      </StyledModalContainer>
    </StyledBackdrop>
  );
}

export default HardwareSettingsModal;
